import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ListingCard } from "@/components/ListingCard";
import { AgentProofTeaser } from "@/components/landing/AgentProofTeaser";
import { ConsumerTrustSection } from "@/components/landing/ConsumerTrustSection";
import { ConsumerJourneyGrid } from "@/components/landing/ConsumerJourneyGrid";
import { DualAudienceHero } from "@/components/landing/DualAudienceHero";
import { HomepageDecisionSupportSection } from "@/components/landing/HomepageDecisionSupportSection";
import { MarketActivityBanner } from "@/components/landing/MarketActivityBanner";
import { NewLaunchPreviewSection } from "@/components/landing/NewLaunchPreviewSection";
import { RealTimeAssetIntelligenceStrip } from "@/components/landing/RealTimeAssetIntelligenceStrip";
import { SearchBar } from "@/components/SearchBar";
import { SellerLeadMagnet } from "@/components/SellerLeadMagnet";
import { SignatureAgentLandingPage } from "@/components/SignatureAgentLandingPage";
import { SignatureInviteOnlyLandingPage } from "@/components/SignatureInviteOnlyLandingPage";
import { AgentPortalUnavailable } from "@/components/AgentPortalUnavailable";
import { QUICK_DISTRICTS } from "@/data/portalConfig";
import { useSiteBranding } from "@/context/SiteContext";
import { useFastPolling } from "@/hooks/useFastPolling";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/portalHelpers";
import { getRootOnlyBoolean, getRootOnlyClassName } from "@/lib/routeIsolation";

const defaultOptions = { districts: [], tenures: [], property_types: [] };
const defaultFilters = { district: "", tenure: "", property_type: "" };
export const HomePage = () => {
  const navigate = useNavigate();
  const { branding, buildBrandedPath, getWhatsAppHref, leadFormPath, isAgentPath, requestParams, siteContext } = useSiteBranding();
  const homepageRequestRef = useRef({ key: null, promise: null });
  const isSignatureAgentPath = isAgentPath && branding.agent_theme === "signature";
  const isInviteOnlySignaturePath = isSignatureAgentPath && siteContext.invite_only;
  const isMissingAgentPortal = isAgentPath && siteContext.mode === "global" && Boolean(siteContext.requested_slug);
  const rootOnlyShellClass = getRootOnlyClassName(isAgentPath, "homepage-institutional");
  const shouldUseInstitutionalSearch = getRootOnlyBoolean(isAgentPath);
  const [options, setOptions] = useState(defaultOptions);
  const [filters, setFilters] = useState(defaultFilters);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loadingHomepage, setLoadingHomepage] = useState(true);
  const [inventorySummary, setInventorySummary] = useState({ cleanTotal: 139, multiAgentPropertyTotal: 128 });
  const [foundingRibbon, setFoundingRibbon] = useState({ active_count: 1, available_count: 17, members: [] });
  const [agentHomepageMetrics, setAgentHomepageMetrics] = useState({ market_average_yield_pct: null, portfolio_yield_pct: null, under_valued_unit_count: 0, listing_count: 0 });
  const featuredInsight = insights[0] || null;
  const openLivePortal = useCallback(() => {
    if (typeof window === "undefined") return;
    const nextUrl = new URL("/agent/clive", window.location.origin).toString();
    window.location.assign(nextUrl);
  }, []);

  useSeo({
    title: "SG Prop Portal | Buy, Rent, New Launches & Financials",
    description: "Team-led Singapore property portal with live district search, all 28 district landing pages, financial calculators, and direct WhatsApp or email access.",
  });

  const loadHomepage = useCallback(async () => {
    setLoadingHomepage(true);
    const requestKey = JSON.stringify({ isSignatureAgentPath, requestParams });
    try {
      if (isSignatureAgentPath) {
        const activeRequest =
          homepageRequestRef.current.key === requestKey && homepageRequestRef.current.promise
            ? homepageRequestRef.current.promise
            : Promise.all([
                api.get("/search-options"),
                api.get("/listings/homepage-snapshot", { params: { limit: 500, ...requestParams } }),
              ]);
        homepageRequestRef.current = { key: requestKey, promise: activeRequest };
        const [optionsResponse, snapshotResponse] = await activeRequest;

        setOptions(optionsResponse.data);
        setFeaturedListings(snapshotResponse.data?.items || []);
        setAgentHomepageMetrics(snapshotResponse.data?.metrics || { market_average_yield_pct: null, portfolio_yield_pct: null, under_valued_unit_count: 0, listing_count: 0 });
        setInsights([]);
        setInventorySummary({ cleanTotal: 139, multiAgentPropertyTotal: 128 });
        setFoundingRibbon({ active_count: 1, available_count: 17, members: [] });
        return;
      }

      const activeRequest =
        homepageRequestRef.current.key === requestKey && homepageRequestRef.current.promise
          ? homepageRequestRef.current.promise
          : Promise.all([
              api.get("/search-options"),
              api.get("/listings", { params: { limit: isSignatureAgentPath ? 500 : 6, ...(isSignatureAgentPath ? {} : { featured: true }), ...requestParams } }),
              api.get("/insights"),
              api.get("/founding-members/ribbon"),
              api.get("/listings/public", { params: { page: 1, page_size: 1 } }),
            ]);
      homepageRequestRef.current = { key: requestKey, promise: activeRequest };
      const [optionsResponse, listingsResponse, insightsResponse, foundingRibbonResponse, inventoryResponse] = await activeRequest;

      setOptions(optionsResponse.data);
      setFeaturedListings(listingsResponse.data);
      setInsights(insightsResponse.data.slice(0, 5));
      setFoundingRibbon(foundingRibbonResponse.data);
      setInventorySummary({
        cleanTotal: Number(inventoryResponse.data?.clean_total || inventoryResponse.data?.total || 139),
        multiAgentPropertyTotal: Number(inventoryResponse.data?.multi_agent_property_total || 128),
      });
    } catch {
      setOptions(defaultOptions);
      setFeaturedListings([]);
      setInsights([]);
      setInventorySummary({ cleanTotal: 139, multiAgentPropertyTotal: 128 });
      setFoundingRibbon({ active_count: 1, available_count: 17, members: [] });
      setAgentHomepageMetrics({ market_average_yield_pct: null, portfolio_yield_pct: null, under_valued_unit_count: 0, listing_count: 0 });
    } finally {
      if (homepageRequestRef.current.key === requestKey) {
        homepageRequestRef.current = { key: null, promise: null };
      }
      setLoadingHomepage(false);
    }
  }, [isSignatureAgentPath, requestParams]);

  useEffect(() => {
    loadHomepage();
  }, [loadHomepage]);

  useFastPolling(loadHomepage, 12000, !isSignatureAgentPath);

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.district) params.set("district", filters.district);
    if (filters.tenure) params.set("tenure", filters.tenure);
    if (filters.property_type) params.set("property_type", filters.property_type);

    if (filters.district) {
      const districtParams = new URLSearchParams();
      if (filters.tenure) districtParams.set("tenure", filters.tenure);
      if (filters.property_type) districtParams.set("property_type", filters.property_type);
      navigate(buildBrandedPath(`/district/${filters.district}${districtParams.toString() ? `?${districtParams.toString()}` : ""}`));
      return;
    }

    navigate(buildBrandedPath(`/listings${params.toString() ? `?${params.toString()}` : ""}`));
  }, [buildBrandedPath, filters.district, filters.property_type, filters.tenure, navigate]);

  const handleInstitutionalSearch = useCallback(
    (query) => {
      const params = new URLSearchParams();
      params.set("unique_listings", "true");
      if (query?.trim()) params.set("q", query.trim());
      navigate(buildBrandedPath(`/listings?${params.toString()}`));
    },
    [buildBrandedPath, navigate],
  );

  const startPublicFoundingCheckout = useCallback(async () => {
    try {
      const response = await api.post("/billing/public-founding-seat-checkout", { origin_url: window.location.origin });
      if (response.data?.url) {
        window.location.assign(response.data.url);
      }
    } catch (error) {
      window.open(getWhatsAppHref("Hello SG Prop Portal, I want to secure a Founding Seat."), "_blank", "noopener,noreferrer");
    }
  }, [getWhatsAppHref]);

  if (isSignatureAgentPath) {
    if (isInviteOnlySignaturePath) {
      return <SignatureInviteOnlyLandingPage branding={branding} dashboardPath={siteContext.dashboard_path} inviteOnlyMessage={siteContext.invite_only_message} />;
    }
    return (
      <SignatureAgentLandingPage
        branding={branding}
        buildBrandedPath={buildBrandedPath}
        filters={filters}
        getWhatsAppHref={getWhatsAppHref}
        handleSearch={handleSearch}
        leadFormPath={leadFormPath}
        districtIntelligenceFeed={siteContext.district_intelligence}
        listings={featuredListings}
        portfolioMetrics={agentHomepageMetrics}
        isLoading={loadingHomepage}
        onFiltersChange={setFilters}
        onResetFilters={() => setFilters(defaultFilters)}
        options={options}
      />
    );
  }

  if (isMissingAgentPortal) {
    return <AgentPortalUnavailable requestedSlug={siteContext.requested_slug} />;
  }

  return (
    <div className={rootOnlyShellClass}>
      <DualAudienceHero
        buildBrandedPath={buildBrandedPath}
        foundingRibbon={foundingRibbon}
        getWhatsAppHref={getWhatsAppHref}
        inventorySummary={inventorySummary}
        isAgentPortal={isAgentPath}
        openLivePortal={openLivePortal}
      />

      <MarketActivityBanner uniqueCount={inventorySummary.cleanTotal} />

      <ConsumerJourneyGrid buildBrandedPath={buildBrandedPath} />

      <section className="section-shell pt-8" data-testid="homepage-search-shell">
        <div className="rounded-[36px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_18px_50px_rgba(0,25,53,0.06)] md:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Start with live inventory</p>
              <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="homepage-search-heading">
                Search live Singapore opportunities.
              </h2>
              <p className="mt-4 text-sm leading-8 text-[#42566f] md:text-base" data-testid="homepage-search-copy">
                Compare district, tenure, and property type in one cleaner search flow.
              </p>
            </div>
            {featuredInsight ? (
              <Link
                className="group inline-flex max-w-full flex-wrap items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-[#fffaf1] px-4 py-3 shadow-[0_12px_32px_rgba(0,25,53,0.08)] transition hover:-translate-y-0.5 hover:border-[#D4AF37]"
                data-testid="homepage-featured-insight-cta"
                to={buildBrandedPath(`/insights/${featuredInsight.slug || featuredInsight.id}`)}
              >
                <span className="rounded-full bg-[#fff3d8] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="homepage-featured-insight-badge">
                  Latest Insight
                </span>
                <span className="max-w-[24rem] text-sm font-semibold leading-6 text-[#001935]" data-testid="homepage-featured-insight-headline">
                  {featuredInsight.title}
                </span>
                <ArrowRight className="h-4 w-4 text-[#8b6d16] transition group-hover:translate-x-0.5" />
              </Link>
            ) : null}
          </div>

          <div className="mt-6">
            <SearchBar filters={filters} institutional={shouldUseInstitutionalSearch} onApply={handleSearch} onFiltersChange={setFilters} onReset={() => setFilters(defaultFilters)} options={options} testIdPrefix="homepage-search" />
          </div>
        </div>
      </section>

      <ConsumerTrustSection buildBrandedPath={buildBrandedPath} />

      <section className="section-shell pt-8" data-testid="homepage-featured-listings-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Featured Listings</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="homepage-featured-listings-heading">
              Live Listings You Can Act On Now.
            </h2>
          </div>
          <Link className="outline-button" data-testid="homepage-see-all-listings-link" to={buildBrandedPath("/listings")}>
            See More Listings
          </Link>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          {featuredListings.slice(0, 3).map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      </section>

      <section className="section-shell pt-2">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="subtle-label">Explore by District</p>
            <h2 className="section-heading mt-2">Explore Singapore by District.</h2>
          </div>
          <Link className="outline-button" data-testid="homepage-all-districts-link" to={buildBrandedPath("/listings")}>
            Browse All Districts
          </Link>
        </div>
        <div className="mt-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-7">
          {QUICK_DISTRICTS.map((district) => (
            <Link className="homepage-institutional-chip rounded-2xl border px-5 py-4 text-center text-sm font-semibold transition hover:border-[#D4AF37]/70" data-testid={`homepage-district-chip-${district.toLowerCase()}`} key={district} to={buildBrandedPath(`/district/${district}`)}>
              {district}
            </Link>
          ))}
        </div>
      </section>

      <section className="section-shell pt-4">
        <RealTimeAssetIntelligenceStrip />
      </section>

      <NewLaunchPreviewSection buildBrandedPath={buildBrandedPath} listings={featuredListings} />

      <HomepageDecisionSupportSection buildBrandedPath={buildBrandedPath} />

      <AgentProofTeaser buildBrandedPath={buildBrandedPath} openLivePortal={openLivePortal} />

      <section className="section-shell">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="subtle-label">Latest Market Insights</p>
            <h2 className="section-heading mt-2">Fresh official signals, reframed for active buyers.</h2>
          </div>
          <Link className="outline-button" data-testid="homepage-news-link" to={buildBrandedPath("/insights")}>
            Browse All Insights
          </Link>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          {insights.slice(0, 3).map((article) => (
            <Link className="luxury-panel overflow-hidden transition hover:-translate-y-1" data-testid={`homepage-news-card-${article.id}`} key={article.id} to={buildBrandedPath(`/insights/${article.slug || article.id}`)}>
              <img alt={article.title} className="aspect-[1.35/1] w-full object-cover" src={article.image_url} />
              <div className="space-y-4 p-5">
                <p className="text-sm text-slate-400">{formatDate(article.published_at)}</p>
                <h3 className="text-2xl font-semibold text-white">{article.title}</h3>
                <p className="text-sm leading-7 text-slate-300">{article.excerpt}</p>
              </div>
            </Link>
          ))}

          {!insights.length ? (
            <div className="luxury-panel p-6 lg:col-span-3" data-testid="homepage-insights-empty-state">
              <p className="subtle-label">Preparing the first release</p>
              <h3 className="mt-2 text-3xl font-semibold text-white">The live insights hub will populate once our editorial team publishes the new drafts.</h3>
              <p className="mt-4 max-w-3xl text-sm leading-8 text-slate-300">
                The admin CMS already holds the latest URA and HDB draft articles, ready for review and manual publishing.
              </p>
            </div>
          ) : null}
        </div>
      </section>

      <section className="section-shell">
        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-5">
            <p className="subtle-label">Seller / Landlord Support</p>
            <h2 className="section-heading">Own a property and want better guidance?</h2>
            <p className="section-copy">
              Request valuation support, discuss leasing plans, or speak directly with an advisor about your next property move.
            </p>
            <div className="luxury-panel p-6">
              <h3 className="text-2xl font-semibold text-white">Why owners reach out early</h3>
              <ul className="mt-4 grid gap-3 text-sm leading-7 text-slate-300">
                <li>• Pricing guidance anchored to current market movement and district inventory.</li>
                <li>• A cleaner sell / lease-out split so owners know the next step immediately.</li>
                <li>• A branded 2026 Market Insights PDF to capture high-intent seller leads.</li>
                <li>• One direct line for WhatsApp, email, and follow-ups instead of scattered handovers.</li>
              </ul>
            </div>
          </div>

          <SellerLeadMagnet
            contextLabel="Homepage seller section"
            description="Request a free property valuation or unlock the 2026 Market Insights PDF to understand current pricing signals, buyer behaviour, and seller positioning."
            heading="Request valuation and owner guidance in one place."
            testIdPrefix="homepage-seller-magnet"
          />
        </div>
      </section>

      <section className="section-shell pb-14 pt-8" data-testid="homepage-final-cta-section">
        <div className="rounded-[34px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 text-center shadow-[0_28px_80px_rgba(0,25,53,0.1)]">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Final Call To Action</p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl">Ready to take the next step?</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link className="gold-button" data-testid="homepage-final-browse-button" to={buildBrandedPath("/listings")}>
              Browse Listings <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link className="outline-button border-[#001935]/14 text-[#001935] hover:bg-[#001935]/5" data-testid="homepage-final-contact-button" to={buildBrandedPath("/contact")}>
              Contact Us
            </Link>
            <a className="outline-button border-[#001935]/14 text-[#001935] hover:bg-[#001935]/5" data-testid="homepage-final-whatsapp-button" href={getWhatsAppHref("Hello SG Prop Portal, I would like help with my property search.")} rel="noreferrer" target="_blank">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};